import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { GenericEmbedComponentProps, ParamMappingConfig } from './types'
import { buildTargetUrl } from './utils/resolveParams'
import useStyleMaps from 'site/Renderer/hooks/useStyleMap'
import { useSitemodelState } from 'sitemodel/shared/hooks'
import { IframeContainer, LoadingContainer, ErrorContainer } from './styles'

interface GenericEmbedState {
  resolvedUrl: string | null
  isLoading: boolean
  error: string | null
}

const GenericEmbedComponent: React.FC<{
  props: GenericEmbedComponentProps['props']
}> = ({
  props: {
    target_url: targetUrl,
    iframe_height: iframeHeight,
    iframe_width: iframeWidth,
    params_mapping: paramsMapping,
    styleMap
  }
}) => {
  const [searchParams] = useSearchParams()
  const { sitemodelState } = useSitemodelState()
  const styleProps = useStyleMaps(styleMap)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const cleanupRef = useRef<(() => void) | null>(null)

  const [embedState, setEmbedState] = useState<GenericEmbedState>({
    resolvedUrl: null,
    isLoading: true,
    error: null
  })

  const handleIframeLoad = () => {
    // Clean up any previous observers/listeners
    if (cleanupRef.current) {
      cleanupRef.current()
      cleanupRef.current = null
    }

    if (!iframeRef.current || !containerRef.current) return

    try {
      const iframeDoc =
        iframeRef.current.contentDocument ||
        iframeRef.current.contentWindow?.document

      if (iframeDoc && iframeDoc.body) {
        let observer: MutationObserver | null = null
        let timeoutId: NodeJS.Timeout | null = null
        let resizeListener: (() => void) | null = null

        const resizeContainer = () => {
          if (!iframeRef.current || !containerRef.current) return

          const contentHeight = Math.max(
            iframeDoc.documentElement.scrollHeight || 0,
            iframeDoc.body.scrollHeight || 0,
            iframeDoc.documentElement.clientHeight || 0,
            iframeDoc.body.clientHeight || 0
          )

          if (contentHeight > 0) {
            containerRef.current.style.height = `${contentHeight}px`
            iframeRef.current.style.height = `${contentHeight}px`
          }
        }

        resizeContainer()

        timeoutId = setTimeout(resizeContainer, 500)

        observer = new MutationObserver(resizeContainer)
        observer.observe(iframeDoc.body, {
          childList: true,
          subtree: true,
          attributes: true
        })

        resizeListener = () => resizeContainer()
        try {
          iframeDoc.defaultView?.addEventListener('resize', resizeListener)
        } catch (e) {
          null
        }

        cleanupRef.current = () => {
          if (observer) {
            observer.disconnect()
          }
          if (timeoutId) {
            clearTimeout(timeoutId)
          }
          if (resizeListener && iframeDoc.defaultView) {
            try {
              iframeDoc.defaultView.removeEventListener(
                'resize',
                resizeListener
              )
            } catch (e) {
              null
            }
          }
        }
      }
    } catch (e) {
      if (!iframeHeight && containerRef.current && iframeRef.current) {
        const defaultHeight = '600px'
        containerRef.current.style.height = defaultHeight
        iframeRef.current.style.height = defaultHeight
      }
    }
  }

  useEffect(() => {
    const resolveUrl = async () => {
      try {
        if (!targetUrl) {
          setEmbedState({
            resolvedUrl: null,
            isLoading: false,
            error: 'No target URL provided'
          })
          return
        }

        const resolvedUrl = buildTargetUrl({
          baseUrl: targetUrl,
          paramsMapping: paramsMapping as Record<string, ParamMappingConfig>,
          sitemodelState,
          searchParams
        })

        setEmbedState({
          resolvedUrl,
          isLoading: false,
          error: null
        })
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to resolve URL'
        setEmbedState({
          resolvedUrl: null,
          isLoading: false,
          error: errorMessage
        })
      }
    }

    resolveUrl()
  }, [targetUrl, paramsMapping, sitemodelState, searchParams])

  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current()
        cleanupRef.current = null
      }
    }
  }, [embedState.resolvedUrl])

  if (embedState.isLoading) {
    return <LoadingContainer>Loading embedded content...</LoadingContainer>
  }

  if (embedState.error) {
    return <ErrorContainer>Error: {embedState.error}</ErrorContainer>
  }

  if (!embedState.resolvedUrl) {
    return null
  }

  return (
    <IframeContainer
      ref={containerRef}
      $height={iframeHeight}
      $width={iframeWidth}
      style={styleProps}
      data-testid='generic-embed-container'
    >
      <iframe
        ref={iframeRef}
        src={embedState.resolvedUrl}
        title='Embedded Content'
        data-testid='generic-embed-iframe'
        sandbox='allow-same-origin allow-scripts allow-popups allow-forms allow-pointer-lock'
        scrolling='no'
        onLoad={handleIframeLoad}
      />
    </IframeContainer>
  )
}

export default GenericEmbedComponent
